import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';

import Animation from 'components/lottie-animations/favorite-loading/favorite-loading';
import Picture from 'components/picture';
import { IncentiveSource } from 'state/loyalty/types';
import noop from 'utils/noop';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';
import { ParsedRewardData } from '../incentive-details/types';

import {
  BackgroundImage,
  CardContainer,
  FavoriteButton,
  ImageWrapper,
  InfoContainer,
  LoadingWrapper,
  RewardText,
  StyledBadge,
  StyledClickableContainer,
  StyledRewardLock,
} from './incentive-card.styled';

export interface IIncentiveCard {
  incentiveData: ParsedRewardData;
  handleClick: ILoyaltyRewardsViewProps['handleClick'];
  children?: React.ReactNode;
  locked?: boolean;
  favorited?: boolean;
  favoriteEnabled?: boolean;
  favoriteLoading?: boolean;
  handleToggleFavorite?: (incentiveId: string) => void;
}

export const IncentiveCard: FC<IIncentiveCard> = memo(
  ({
    incentiveData,
    handleClick,
    children,
    locked = false,
    favorited = false,
    favoriteEnabled = false,
    favoriteLoading = false,
    handleToggleFavorite = noop,
  }) => {
    const { formatMessage } = useIntl();
    const {
      image,
      imageDescription,
      name,
      id,
      loyaltyEngineId,
      backgroundImage,
      metadata,
    } = incentiveData;

    // Offers will use the EngineId as the URL identifier because the SanityId might be the same for multiple PersonalizedOffers.
    // Rewards will use the SanityId until we are sure we don't have any deep links to specific Rewards.
    const incentiveSanityId = id;
    const incentiveName = name;

    const showPromoCodeLabel = metadata?.some(
      data => data?.value === IncentiveSource.PROMOTION_CODE
    );
    const promoCodeLabel = showPromoCodeLabel
      ? formatMessage({ id: 'promoCodeBadgeLabel' })
      : undefined;

    const handleFavoriteClick = useCallback(
      event => {
        event.preventDefault();
        event.stopPropagation();
        handleToggleFavorite(loyaltyEngineId);
      },
      [handleToggleFavorite, loyaltyEngineId]
    );

    return (
      <CardContainer>
        <StyledClickableContainer
          onClick={handleClick({ incentiveName, loyaltyEngineId, incentiveSanityId })}
          isLocked={locked}
        >
          {image && (
            <ImageWrapper>
              {locked && <StyledRewardLock />}
              {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
              <Picture image={image} alt={imageDescription} {...incentiveCardImageDefaultProps} />
            </ImageWrapper>
          )}

          <InfoContainer>
            <RewardText>{name}</RewardText>
            {children}
            {promoCodeLabel && (
              <StyledBadge>
                <Icon icon="offer" color="black" width="18px" height="18px" />
                {promoCodeLabel}
              </StyledBadge>
            )}
          </InfoContainer>
          {favoriteEnabled && (
            <FavoriteButton onClick={handleFavoriteClick} data-testid="favorite-button">
              {favoriteLoading ? (
                <LoadingWrapper>
                  <Animation />
                </LoadingWrapper>
              ) : (
                <Icon
                  aria-hidden
                  color="icon-default"
                  height="3rem"
                  icon={favorited ? 'favoriteFill' : 'favorite'}
                  width="2rem"
                />
              )}
            </FavoriteButton>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
